import React, { useState } from 'react';
import { Send, Calendar, Mail, Phone, User } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/bookings`, formData);
      
      if (response.data.success) {
        toast.success('Booking request sent! We\'ll contact you soon!');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventDate: '',
          eventType: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Oops! Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-4 bg-gradient-to-br from-cream-bg via-logo-teal-light to-white">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-logo-navy vintage-title mb-4">
            Book Your Event
          </h2>
          <p className="text-xl text-logo-navy-light max-w-2xl mx-auto">
            Ready to create lasting memories? Fill out the form below and we'll get back to you shortly!
          </p>
          <div className="mt-6 inline-block bg-logo-teal/10 border-2 border-logo-teal px-6 py-3 rounded-lg">
            <p className="text-logo-navy font-semibold">
              📍 Delivery within 20 miles of 92101
            </p>
          </div>
        </div>
        
        <Card className="p-8 md:p-12 bg-white border-2 border-logo-teal/30 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-logo-navy font-semibold mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Your Name *
                </label>
                <Input 
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="border-2 border-logo-teal/30 focus:border-logo-teal"
                />
              </div>
              
              <div>
                <label className="block text-logo-navy font-semibold mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address *
                </label>
                <Input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className="border-2 border-logo-teal/30 focus:border-logo-teal"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-logo-navy font-semibold mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Phone Number *
                </label>
                <Input 
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="(555) 123-4567"
                  className="border-2 border-logo-teal/30 focus:border-logo-teal"
                />
              </div>
              
              <div>
                <label className="block text-logo-navy font-semibold mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Event Date *
                </label>
                <Input 
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  required
                  className="border-2 border-logo-teal/30 focus:border-logo-teal"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-logo-navy font-semibold mb-2">
                Event Type *
              </label>
              <Input 
                type="text"
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                required
                placeholder="Wedding, Birthday, Corporate Event, etc."
                className="border-2 border-logo-teal/30 focus:border-logo-teal"
              />
            </div>
            
            <div>
              <label className="block text-logo-navy font-semibold mb-2">
                Additional Details
              </label>
              <Textarea 
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                placeholder="Tell us about your event, preferred package, special requests, etc."
                className="border-2 border-logo-teal/30 focus:border-logo-teal"
              />
            </div>
            
            <Button 
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-accent-warm hover:bg-accent-warm/90 text-white py-6 text-lg font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              {isSubmitting ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2 inline" />
                  Send Booking Request
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
};

export default ContactForm;